'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import { Zu, $u, ed, Scene_a, Qu, PublishIllustration } from './product-video';
import { MarketReveal, zl } from './collection-animation';
import { MarketImage } from './market-primitives';
import { ShowcaseIcon, SceneNotifications } from './scene-primitives';
import { marketFacts } from './workflow-facts';
import { LisiereLogo } from './reference-brands';
export function PublishApproval({ t: e, reduced: t }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let [n, r] = React.useState(null);
  React.useEffect(() => {
    if (e !== 0) return;
    const frame = requestAnimationFrame(() => r(null));
    return () => cancelAnimationFrame(frame);
  }, [e]);
  let i = !t && n === null,
    o = n ?? (i && e >= Zu.decide ? 'approved' : 'pending'),
    s = o === 'pending' ? null : $u[o],
    c = e >= Zu.items ? [ed, ...(s ? [s.toast] : [])] : [];
  return (
    <div
      className={
        'mx-auto flex w-full max-w-[640px] flex-col gap-4 py-2 sm:py-6'
      }
    >
      <MarketReveal on={e >= Zu.card} className={'mk-card grid p-4 sm:p-5'}>
        <div
          className={'panel'}
          data-active={o === 'pending'}
          inert={o !== 'pending'}
        >
          <div className={'text-ink-3 flex items-center gap-2 text-[12.5px]'}>
            <span
              className={
                'grid size-6 place-items-center rounded-full bg-[var(--primary)] text-[var(--primary-foreground)]'
              }
            >
              <Scene_a className={'h-[8px] w-auto'} />
            </span>
            {translateShowcaseNode(showcaseCopy, 'Needs your approval')}
            <span className={'text-ink-4 ml-auto truncate max-sm:hidden'}>
              {translateShowcaseNode(
                showcaseCopy,
                'Maison Aubrac \xB7 FW27 launch'
              )}
            </span>
          </div>
          <p
            className={
              'text-ink mt-3 text-[18px] leading-6 font-medium tracking-[-0.012em] text-balance sm:text-[20px] sm:leading-7'
            }
          >
            {translateShowcaseNode(
              showcaseCopy,
              'Publish to your store and social account?'
            )}
          </p>
          <ul className={'mt-4 flex flex-col'}>
            {translateShowcaseNode(
              showcaseCopy,
              Qu.map((t, n) => (
                <MarketReveal
                  key={t.name}
                  as={'li'}
                  on={e >= Zu.items + n * 120}
                  className={
                    'flex items-center gap-3 border-t border-[var(--line)] py-2 first:border-t-0'
                  }
                >
                  <MarketImage
                    asset={t.asset}
                    zoom={t.zoom}
                    focus={t.focus}
                    className={'size-10 shrink-0 rounded-[6px]'}
                  />
                  <span className={'min-w-0 flex-1'}>
                    <span
                      className={
                        'text-ink block truncate text-[14px] leading-5'
                      }
                    >
                      {translateShowcaseNode(showcaseCopy, t.name)}
                    </span>
                    <span
                      className={
                        'text-ink-3 flex items-center gap-1 text-[12px] leading-4'
                      }
                    >
                      <ShowcaseIcon name={t.icon} className={'size-3.5'} />
                      {translateShowcaseNode(showcaseCopy, t.to)}
                    </span>
                  </span>
                </MarketReveal>
              ))
            )}
          </ul>
          <p className={'text-ink-3 mt-3 text-[13px] leading-5 text-pretty'}>
            {translateShowcaseNode(showcaseCopy, marketFacts.approval)}
          </p>
          <MarketReveal
            on={e >= Zu.actions}
            className={'mt-4 flex flex-wrap items-center justify-end gap-2'}
          >
            <button
              type={'button'}
              className={'btn ghost press'}
              onClick={() => r('declined')}
            >
              {translateShowcaseNode(showcaseCopy, 'Decline')}
            </button>
            <button
              type={'button'}
              className={'btn btn-soft press'}
              onClick={() => r('edit')}
            >
              {translateShowcaseNode(showcaseCopy, 'Edit first')}
            </button>
            <span className={'relative'}>
              <button
                type={'button'}
                className={`btn btn-ink press ${i && e >= Zu.press && e < Zu.decide ? 'scale-[0.97]' : ''}`}
                onClick={() => r('approved')}
              >
                {translateShowcaseNode(showcaseCopy, 'Approve')}
              </button>
              {translateShowcaseNode(
                showcaseCopy,
                i ? <PublishIllustration t={e} /> : null
              )}
            </span>
          </MarketReveal>
        </div>
        <div
          className={
            'panel flex flex-col items-center justify-center gap-3 text-center'
          }
          data-active={o !== 'pending'}
          inert={o === 'pending'}
        >
          <span
            className={
              'grid size-11 place-items-center rounded-full bg-[var(--primary)] text-[var(--primary-foreground)]'
            }
          >
            {translateShowcaseNode(
              showcaseCopy,
              s ? (
                <ShowcaseIcon
                  key={o}
                  name={s.icon}
                  draw={'now'}
                  className={'size-5'}
                  strokeWidth={2}
                  style={{
                    '--d': '160ms',
                  }}
                />
              ) : null
            )}
          </span>
          <p className={'text-ink text-[17px] font-medium'}>
            {translateShowcaseNode(showcaseCopy, s?.line ?? '')}
          </p>
          <ul
            className={'flex flex-wrap justify-center gap-2'}
            aria-label={translateShowcaseNode(showcaseCopy, 'Launch items')}
          >
            {translateShowcaseNode(
              showcaseCopy,
              Qu.map((e) => (
                <li
                  key={e.name}
                  className={
                    'text-ink-3 flex items-center gap-2 rounded-full bg-[var(--state-wash)] py-1 pr-3 pl-1 text-[12px] leading-4'
                  }
                >
                  <MarketImage
                    asset={e.asset}
                    zoom={e.zoom}
                    focus={e.focus}
                    className={'size-6 rounded-full'}
                  />
                  {translateShowcaseNode(showcaseCopy, e.short)}
                  <span className={'text-ink font-medium'}>
                    {translateShowcaseNode(
                      showcaseCopy,
                      o === 'approved' ? 'Scheduled' : 'Draft'
                    )}
                  </span>
                </li>
              ))
            )}
          </ul>
          <button
            type={'button'}
            onClick={() => r('pending')}
            className={
              'ghost press flex h-10 items-center gap-1.5 px-3 text-[13px] font-medium'
            }
          >
            <ShowcaseIcon name={'replay'} className={'size-4'} />
            {translateShowcaseNode(showcaseCopy, 'Ask again')}
          </button>
        </div>
      </MarketReveal>
      <SceneNotifications
        toasts={c}
        label={'Launch updates'}
        className={'mx-auto w-full max-w-[360px]'}
      />
    </div>
  );
}
export const rd = '#1C1A17';
export const id = '#F7F4EE';
export const ad = '#8B7E6E';
export const od = '#D8CFC0';
export const sd = '#3A342D';
export function BuyerSlideHeading({ title: e, slide: t, children: n }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div
      className={'mk-page relative flex size-full flex-col p-[2.2em]'}
      style={{
        background: id,
        color: rd,
      }}
    >
      <div
        className={'flex items-baseline justify-between'}
        style={{
          color: ad,
        }}
      >
        <span className={'flex items-center gap-[0.6em] text-[0.75em]'}>
          <LisiereLogo className={'h-[1.1em] w-auto'} />
          <span className={'mk-caps'}>
            {translateShowcaseNode(showcaseCopy, 'FW27')}
          </span>
        </span>
        <span className={'mk-caps num text-[0.75em]'}>
          {translateShowcaseNode(showcaseCopy, String(t).padStart(2, '0'))}
          {translateShowcaseNode(showcaseCopy, ' / 11')}
        </span>
      </div>
      <h3 className={'mk-serif mt-[0.5em] text-[2.3em] leading-[1.05]'}>
        {translateShowcaseNode(showcaseCopy, e)}
      </h3>
      <div className={'mt-[1em] min-h-0 flex-1'}>
        {translateShowcaseNode(showcaseCopy, n)}
      </div>
    </div>
  );
}
export function BuyerSlideTable({ head: e, rows: t, lt: n, cols: r }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div className={'num text-[0.9em]'}>
      <div
        className={'grid gap-[0.8em] pb-[0.45em]'}
        style={{
          gridTemplateColumns: r,
          color: ad,
          borderBottom: `1px solid ${od}`,
        }}
      >
        {translateShowcaseNode(
          showcaseCopy,
          e.map((e, t) => (
            <span key={t} className={'mk-caps min-w-0 truncate text-[0.8em]'}>
              {translateShowcaseNode(showcaseCopy, e)}
            </span>
          ))
        )}
      </div>
      {translateShowcaseNode(
        showcaseCopy,
        t.map((e, t) => (
          <MarketReveal
            key={e.key}
            on={n >= 140 + t * 110}
            className={`grid items-center gap-[0.8em] py-[0.34em] ${e.total ? 'font-medium' : ''}`}
            style={{
              gridTemplateColumns: r,
              borderBottom: e.total ? 'none' : `1px solid ${od}`,
              borderTop: e.total ? `1px solid ${rd}` : void 0,
            }}
          >
            {translateShowcaseNode(
              showcaseCopy,
              e.cells.map((e, t) => (
                <span key={t} className={'min-w-0 truncate'}>
                  {translateShowcaseNode(showcaseCopy, e)}
                </span>
              ))
            )}
          </MarketReveal>
        ))
      )}
    </div>
  );
}
export function ConceptBadge() {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <span
      className={
        'mk-caps absolute bottom-[0.6em] left-[0.6em] rounded-[0.3em] px-[0.5em] py-[0.15em] text-[0.6em]'
      }
      style={{
        background: 'rgb(247 244 238 / 0.85)',
        color: ad,
      }}
    >
      {translateShowcaseNode(showcaseCopy, 'Concept view')}
    </span>
  );
}
export const dd = 380;
export const fd = (e) =>
  Math.max(0, Math.min(zl.length - 1, Math.floor(e / dd)));
export const pd = zl.reduce((e, t) => e + t.retail, 0);
export const md = zl.reduce((e, t) => e + t.wholesale, 0);
export const hd = Math.max(...zl.map((e) => e.retail));
